import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  arrearsAgeingQueryOptions,
  buildReportSnapshot,
  collectionsRegisterQueryOptions,
  disbursementsRegisterQueryOptions,
  groupHealthQueryOptions,
  performanceQueryOptions,
  portfolioAtRiskQueryOptions,
  reportBalanceSheetQueryOptions,
  reportIncomeStatementQueryOptions,
  reportSnapshotQueryOptions,
  reportSnapshotsQueryOptions,
  reportsKeys,
  reportTrialBalanceQueryOptions,
} from '@/api/reports-api'
import type {
  ArrearsAgeingQuery,
  BuildReportSnapshotRequest,
  CollectionsRegisterQuery,
  DisbursementsRegisterQuery,
  GroupHealthQuery,
  PerformanceQuery,
  PortfolioAtRiskQuery,
  ReportBalanceSheetQuery,
  ReportIncomeStatementQuery,
  ReportSnapshotListQuery,
  ReportTrialBalanceQuery,
} from '@/api/reports-types'
import { loanOfficersQueryOptions, myBranchesQueryOptions } from '@/api/reference-api'
import type { Branch } from '@/api/types'

/**
 * Reads behind the reports screens.
 *
 * Thin, as `ledger/queries.ts` is thin: each hook is a `useQuery` over the options declared in
 * `reports-api.ts`, and `enabled` is always driven by a permission or a submitted intent — never
 * by "the screen is open". See that file's own note on why.
 */

const NO_BRANCHES: Branch[] = []

export interface ReportsBranchReach {
  branches: Branch[]
  homeBranchId: string | null
  canAccessAllBranches: boolean
  directoryAvailable: boolean
}

/** The branches this user can narrow a report to. Same query the ledger and journal screens use. */
export function useReportsBranchReach(): ReportsBranchReach {
  const query = useQuery(myBranchesQueryOptions())

  return {
    branches: query.data?.items ?? NO_BRANCHES,
    homeBranchId: query.data?.homeBranchId ?? null,
    canAccessAllBranches: query.data?.canAccessAllBranches ?? false,
    directoryAvailable: query.data?.directoryAvailable ?? false,
  }
}

/** The loan officers at a branch, for the officer filter. Empty until a branch is chosen. */
export function useReportsLoanOfficers(branchId: string | null) {
  return useQuery(loanOfficersQueryOptions(branchId))
}

export function useArrearsAgeing(query: ArrearsAgeingQuery, enabled = true) {
  return useQuery(arrearsAgeingQueryOptions(query, enabled))
}

export function usePortfolioAtRisk(query: PortfolioAtRiskQuery, enabled = true) {
  return useQuery(portfolioAtRiskQueryOptions(query, enabled))
}

export function useCollectionsRegister(query: CollectionsRegisterQuery, enabled = true) {
  return useQuery(collectionsRegisterQueryOptions(query, enabled))
}

export function useDisbursementsRegister(query: DisbursementsRegisterQuery, enabled = true) {
  return useQuery(disbursementsRegisterQueryOptions(query, enabled))
}

export function usePerformance(query: PerformanceQuery, enabled = true) {
  return useQuery(performanceQueryOptions(query, enabled))
}

export function useGroupHealth(query: GroupHealthQuery, enabled = true) {
  return useQuery(groupHealthQueryOptions(query, enabled))
}

export function useReportTrialBalance(query: ReportTrialBalanceQuery, enabled = true) {
  return useQuery(reportTrialBalanceQueryOptions(query, enabled))
}

export function useReportBalanceSheet(query: ReportBalanceSheetQuery, enabled = true) {
  return useQuery(reportBalanceSheetQueryOptions(query, enabled))
}

export function useReportIncomeStatement(query: ReportIncomeStatementQuery, enabled = true) {
  return useQuery(reportIncomeStatementQueryOptions(query, enabled))
}

export function useReportSnapshots(query: ReportSnapshotListQuery, enabled = true) {
  return useQuery(reportSnapshotsQueryOptions(query, enabled))
}

export function useReportSnapshot(snapshotId: string, enabled = true) {
  return useQuery(reportSnapshotQueryOptions(snapshotId, enabled))
}

/**
 * Freezes a report as at a date. Invalidates the snapshot list on success so a build made from one
 * tab is visible in another without a manual refresh — the same reasoning every mutation in this
 * application invalidates its own list by.
 */
export function useBuildReportSnapshot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: BuildReportSnapshotRequest) => buildReportSnapshot(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...reportsKeys.all, 'snapshots'] })
    },
  })
}
